import { useLayoutEffect } from "react";
import { useFormContext } from "react-hook-form";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CommonFields from "components/CommonFields";
import { defaultEquipmentFixture } from "fixtures";
import { INPUT_TYPE } from "globalConstants";
import { getLtvFromOwnMargin } from "utils";

const OwnMarginOrLTV = ({ index }: { index: number }) => {
    const { setValue, watch, getFieldState } = useFormContext();
    const disableOwnMargin = watch(`requirement.equipments.${index}.disableOwnMargin`);
    const { error: ownMarginError } = getFieldState(`requirement.equipments.${index}.ownMargin`);
    const { error: LTVError } = getFieldState(`requirement.equipments.${index}.loanToValue`);
    const equipment = watch(`requirement.equipments.${index}`) || defaultEquipmentFixture;
    const ownMargin = equipment.ownMargin;
    const invoiceValue = watch(`requirement.equipments.${index}.invoiceValue`);
    const loanToValue = watch(`requirement.equipments.${index}.loanToValue`);
    const numberOfUnits = watch(`requirement.equipments.${index}.noOfUnits`);

    useLayoutEffect(() => {
        if (disableOwnMargin) {
            setValue(
                `requirement.equipments.${index}.ownMargin`,
                `${(loanToValue >= 100 ? 0 : +(+invoiceValue - (+invoiceValue * loanToValue) / 100).toFixed(2)) * numberOfUnits}`
            );
        } else {
            if (+ownMargin === 0) {
                setValue(`requirement.equipments.${index}.loanToValue`, 0);
            } else {
                setValue(
                    `requirement.equipments.${index}.loanToValue`,
                    +ownMargin > +invoiceValue
                        ? 0
                        : getLtvFromOwnMargin({
                              ownMargin: Number(ownMargin),
                              invoiceValue: Number(invoiceValue),
                          })
                );
            }
        }
    }, [loanToValue, ownMargin, invoiceValue, numberOfUnits, setValue, index, disableOwnMargin]);

    return (
        <Grid container className="common__or__container" justifyContent="center" alignItems="center">
            <Grid item xs={6} alignSelf={ownMarginError ? "flex-start" : "center"}>
                <div className={`or__top or__top--has-subtitle ${ownMarginError ? "or__top--has-error" : ""}`}>
                    <div className={`line ${ownMarginError ? "line--has-error" : ""}`} />
                    <Typography className="common-mui-accordian__text" data-testid={`own-margin-equipment-${index + 1}-field-label`}>
                        Own Margin <span style={{ color: "red" }}> *</span>
                    </Typography>
                </div>
            </Grid>
            <Grid item xs={6}>
                <CommonFields
                    name={`requirement.equipments.${index}.ownMargin`}
                    inputType={INPUT_TYPE.PRICE}
                    label=""
                    subtitle="Downpayment For Your Loan"
                    showLabel={false}
                    data-testid={`own-margin-equipment-${index + 1}-field`}
                    positionHelperTextAbsolute={true}
                    shouldAddMarginTop={false}
                    reactHookFormRegisterOptions={{
                        onChange: (e) => {
                            const value = e.target.value?.split(",").join("");
                            setValue(`requirement.equipments.${index}.ownMargin`, `${value}`);

                            console.log(typeof value, typeof invoiceValue);

                            if (+value === 0) setValue(`requirement.equipments.${index}.loanToValue`, 0);
                            else
                                setValue(
                                    `requirement.equipments.${index}.loanToValue`,
                                    +value > +invoiceValue
                                        ? 0
                                        : getLtvFromOwnMargin({
                                              ownMargin: Number(value),
                                              invoiceValue: Number(invoiceValue),
                                          })
                                );
                            setValue(`requirement.equipments.${index}.disableOwnMargin`, false);
                        },
                        min: 0,
                        max: {
                            value: invoiceValue,
                            message: "* Own Margin cannot be greater than Equipment Cost",
                        },
                    }}
                    readOnly={localStorage.getItem("disable_form") === "true" || (loanToValue && disableOwnMargin)}
                />
            </Grid>
            <Grid item xs={12}>
                <div className="or__circle or__circle--has-subtitle">or</div>
            </Grid>
            <Grid item xs={6} alignSelf={LTVError ? "flex-start" : "center"}>
                <div className={`or__bottom or__bottom--has-subtitle ${LTVError ? "or__bottom--has-error" : ""}`}>
                    <div className="line" />
                    <Typography className="common-mui-accordian__text" data-testid={`ltv-equipment-${index + 1}-field-label`}>
                        Loan to Value (LTV) <span style={{ color: "red" }}>*</span>
                    </Typography>
                </div>
            </Grid>
            <Grid item xs={6}>
                <CommonFields
                    name={`requirement.equipments.${index}.loanToValue`}
                    inputType={INPUT_TYPE.PERCENTAGE}
                    label=""
                    subtitle="Preferred LTV (70 = 70% Loan)"
                    showLabel={false}
                    positionHelperTextAbsolute={true}
                    shouldAddMarginTop={false}
                    data-testid={`ltv-equipment-${index + 1}-field`}
                    reactHookFormRegisterOptions={{
                        onChange: (e) => {
                            const value = +e.target.value;
                            setValue(`requirement.equipments.${index}.loanToValue`, value);

                            setValue(`requirement.equipments.${index}.disableOwnMargin`, true);
                        },
                        min: 0,
                        max: {
                            value: 100,
                            message: "* LTV cannot be greater than 100",
                        },
                    }}
                    readOnly={localStorage.getItem("disable_form") === "true" || (ownMargin && !disableOwnMargin)}
                />
            </Grid>
        </Grid>
    );
};

export default OwnMarginOrLTV;
